import clsx from "clsx";
import { ResponsiveContainer, AreaChart, Area, Tooltip } from "recharts";

type Props = {
  value: number;
  name: string,
  data: Record<string, any>[],
  color?: string
};



export function getPercentageChange(oldNumber, newNumber) {
  var decreaseValue = (oldNumber - newNumber);
  const percentageValue = ((decreaseValue / newNumber) * 100)
  return percentageValue === Infinity ? 100 : percentageValue;
}

const SparkTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0]?.payload;

  return (
    <div className="rounded-lg bg-surface-elevated border border-edge shadow-overlay px-2.5 py-1.5">
      <p className="text-[10px] uppercase tracking-wider text-light-gray">{item?.month}</p>
      <p className="text-[13px] font-bold text-text-primary tabular-nums">{item?.count ?? 0}</p>
    </div>
  );
};

const SimpleAreaChart = ({ value, name, data, color = "#6366F1" }: Props) => {
  const precentageData = getPercentageChange(data[data.length - 1]?.count, data[data.length - 2]?.count)
  const gradientId = `spark-${name?.replace(/\s+/g, "-")}`;

  return (
    <div className="flex rounded-xl flex-col w-full h-full bg-surface-muted border border-edge-faint p-4 smallTablet:p-3 overflow-hidden hover:border-edge transition-colors duration-200 animate-fade-in">
      <p className="text-[13px] font-medium text-light-gray truncate">{name}</p>
      <div className="flex items-baseline gap-2 mt-0.5 smallTablet:flex-wrap smallTablet:gap-x-2 smallTablet:gap-y-0">
        <h1 className="text-[22px] font-bold text-text-primary tracking-tight leading-7 tabular-nums smallTablet:text-[18px]">
          {new Intl.NumberFormat("en-GB").format(value ?? 0)}
        </h1>
        <p className={clsx("text-[11px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-300", precentageData < 0 && "!text-rose-700 dark:!text-rose-300")}>
          {precentageData >= 0 && '+'}{precentageData ? precentageData.toFixed(1) : 0}%
        </p>
      </div>

      <div className="flex-1 min-h-[52px] mt-2 -mx-4 -mb-4 smallTablet:-mx-3 smallTablet:-mb-3">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip content={<SparkTooltip />} cursor={false} />
            <Area
              type="monotone"
              stroke={color}
              strokeWidth={2}
              dataKey="count"
              name={name}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 3, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SimpleAreaChart;
